// Adapted from beautifului.dev (MIT, © 2026 Shane Levine).
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import {
  stashComposerDraft,
  takeComposerDraft,
} from '@/chatContext/composerDraftHandoff';
import {
  ArrowUp,
  Stop,
  Plus,
  X,
  ArrowClockwise,
  Image,
  Paperclip,
  FileText,
  Warning,
} from '@phosphor-icons/react';
import { Capacitor } from '@capacitor/core';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store/store';
import type { ChatMessageAttachment } from '@/slices/chat/chatSlice';
import { isTipDismissed, markTipSeen } from '@/helpers/messageStateGating';
import {
  CHAT_IMAGE_ACCEPT,
  isAcceptedChatImageType,
  MAX_CHAT_IMAGE_BYTES,
  MAX_CHAT_IMAGES,
} from '@/utils/imageTranscode';
import Tooltip from '@/components/ui/tooltip';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import ChatImagePhiReminderModal from '../shared/ChatImagePhiReminderModal';
import { usePendingAttachments } from './usePendingAttachments';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import type { ChatDailyLimits, ChatTurnAttachment } from '@/types/chatStream';
import {
  CHAT_MESSAGE_MAX_CHARS,
  CHAT_MESSAGE_TOO_LONG_NOTICE,
  chatMessageLengthCount,
} from '@/services/chatStream/chatMessageLimit';
import ChatLimitMeter from './ChatLimitMeter';
import {
  type CyclingPlaceholderSpec,
  useCyclingPlaceholder,
} from './useCyclingPlaceholder';
import ChatVoiceInput from './ChatVoiceInput';
import type { PendingAttachmentMethod } from './usePendingAttachments';
import { usePendingDocuments } from './usePendingDocuments';
import {
  CHAT_DOCUMENT_MAX_BYTES,
  isAcceptedChatDocument,
} from '@/services/chatDocuments';
import '../../../../mcgrawHill/mcgrawHillChat.css'; // mcgraw-hill
import { useIsMcGrawHill } from '../../../../mcgrawHill/mcgrawHill'; // mcgraw-hill

export interface ChatComposerAttachmentInput {
  addFiles: (files: FileList | File[], method: PendingAttachmentMethod) => void;
  isAtAttachmentLimit: () => boolean;
  showAttachmentRefusal: () => void;
}

interface ChatComposerProps {
  onSend: (
    message: string,
    attachments: Array<ChatMessageAttachment & ChatTurnAttachment>,
    documentIds: string[],
    documentAttachments: ChatMessageAttachment[]
  ) => boolean | void;
  onStop: () => void;
  /**
   * The box went from empty to holding a draft, or back (ORA-606). Only the
   * flip is reported, not the text: the follow-up chips step aside while the
   * student is writing their own question, and nothing else needs the draft.
   */
  onDraftChange?: (hasDraft: boolean) => void;
  isStreaming: boolean;
  /** A changed anchor is resolving to its own thread. */
  isBlocked?: boolean;
  limits: ChatDailyLimits | null;
  /**
   * The hint in the empty box. A `CyclingPlaceholderSpec` keeps its lead and
   * cycles its endings, each dropping out of the line and the next dropping
   * in, until the student clicks in (the box then shows only its cursor) or
   * starts typing.
   */
  placeholder?: string | CyclingPlaceholderSpec;
  /**
   * Text the composer should open with — "Ask Ora AI" on a highlight seeds it
   * and focuses the box, as does a dashboard preset chip (ORA-449's
   * app-context prefill).
   *
   * It is one-shot: it seeds the box when it appears, and when it is withdrawn
   * (a send consumed it) the box is emptied again unless the student has typed
   * over it. Anything they typed themselves is never touched.
   */
  prefill?: string | null;
  /**
   * The context pills — what Ora can see before the turn is sent (ORA-662).
   * Rendered inside the bar, above the attachment chips, so the whole "what
   * this message carries" story sits in one surface.
   */
  contextPills?: ReactNode;
  /** Lets the conversation-wide drop zone reuse this composer's draft pipeline. */
  attachmentInputRef?: Ref<ChatComposerAttachmentInput>;
  /** Focus only when a surface explicitly becomes visible. */
  autoFocus?: boolean;
  /**
   * The conversation the draft belongs to. Across a surface swap that remounts
   * the composer (the study drawer handing off to the shell's document view,
   * ORA-736) the unsent text follows it to the next composer on that key.
   */
  draftKey?: string;
  /**
   * Why the server refused the last send, said here rather than in the thread
   * (ORA-750). Today only an over-long message: the student has to edit what
   * they wrote, so the box takes their text back and the reason sits beside it.
   */
  sendRejection?: string | null;
}

/** True once the daily allowance is spent, which the server reports per turn. */
const isOutOfChats = (limits: ChatDailyLimits | null): boolean =>
  limits?.chatsRemaining !== null &&
  limits?.chatsRemaining !== undefined &&
  limits.chatsRemaining <= 0;

/** Rows the box grows to before it starts scrolling instead. */
export const MAX_COMPOSER_ROWS = 8;

/** Used when the browser reports `normal` for `line-height` (jsdom reports it). */
const FALLBACK_LINE_HEIGHT_PX = 21;

/**
 * Fit the box to its content, up to `maxRows`, then let it scroll.
 *
 * The height is measured from `auto` every time: `scrollHeight` never shrinks
 * below the height already set, so a box that is not first released stays at
 * the tallest it has ever been and deleting text leaves a gap.
 */
export const resizeComposerTextarea = (
  element: HTMLTextAreaElement,
  maxRows: number = MAX_COMPOSER_ROWS
): void => {
  const styles = window.getComputedStyle(element);
  const lineHeight = parseFloat(styles.lineHeight) || FALLBACK_LINE_HEIGHT_PX;

  const verticalPadding =
    (parseFloat(styles.paddingTop) || 0) +
    (parseFloat(styles.paddingBottom) || 0);

  const maxHeight = lineHeight * maxRows + verticalPadding;

  element.style.height = 'auto';
  const contentHeight = element.scrollHeight;
  element.style.height = `${Math.min(contentHeight, maxHeight)}px`;
  element.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden';
};

/** A string placeholder has nothing to cycle through. */
const NO_CYCLING_TAILS: readonly string[] = [];

/**
 * The prompt bar: one rounded container holding the attachment chips, the
 * text box, and the row of controls (ORA-600).
 *
 * It is one control, not three: the attach, voice and send buttons sit inside
 * the same bordered surface as the text, and the whole thing takes the focus
 * ring together. Send and stop share a slot because they are never both useful
 * — while a turn streams the only thing left to do is stop it.
 */
const ChatComposer = ({
  onSend,
  onStop,
  onDraftChange,
  isStreaming,
  isBlocked = false,
  limits,
  placeholder = 'Ask Ora…',
  prefill = null,
  contextPills,
  attachmentInputRef,
  autoFocus = false,
  draftKey,
  sendRejection = null,
}: ChatComposerProps) => {
  const [value, setValue] = useState('');
  const appliedPrefill = useRef<string | null>(null);
  const valueRef = useRef(value);
  const outOfChats = isOutOfChats(limits);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const focusTextarea = useCallback(() => {
    textareaRef.current?.focus();
  }, []);

  valueRef.current = value;

  useEffect(() => {
    if (draftKey === undefined) return;

    const handedOver = takeComposerDraft(draftKey);
    if (handedOver !== null) setValue((current) => current || handedOver);

    return () => stashComposerDraft(draftKey, valueRef.current);
  }, [draftKey]);

  useEffect(() => {
    if (prefill === appliedPrefill.current) return;

    const previous = appliedPrefill.current;
    appliedPrefill.current = prefill;

    if (prefill !== null) {
      setValue(prefill);
      // A prefill is a student's own action (a highlight, a preset chip), so
      // the cursor goes where they are about to type (ORA-1041).
      if (!isBlocked && !outOfChats) focusTextarea();
      return;
    }

    setValue((current) => (current === previous ? '' : current));
  }, [focusTextarea, isBlocked, outOfChats, prefill]);

  const [notice, setNotice] = useState('');
  const [phiOpen, setPhiOpen] = useState(false);
  const [isDictating, setIsDictating] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const hasDraft = value.trim().length > 0;
  const isDisabled = isBlocked || outOfChats;
  // A disabled box shows its own fixed copy, a focused box shows only its
  // cursor, and a box with text shows the text, so the cycle only runs while
  // the student is looking at an empty box they could type into.
  const cycle = typeof placeholder === 'string' ? null : placeholder;

  const isCycleShown =
    cycle !== null && !isDisabled && !isFocused && value.length === 0;

  const cyclingPlaceholder = useCyclingPlaceholder(
    cycle?.tails ?? NO_CYCLING_TAILS,
    !isCycleShown
  );

  // Disabling a focused box does not reliably fire `blur`, and a stale
  // `isFocused` would leave the box blank once it is enabled again.
  useEffect(() => {
    if (isDisabled) setIsFocused(false);
  }, [isDisabled]);

  // The cycling hint is drawn over the box (a native placeholder cannot
  // animate half of itself), so in that mode the box's own placeholder is
  // blank: a focused empty box shows only its cursor.
  const idlePlaceholder = typeof placeholder === 'string' ? placeholder : '';
  /**
   * The length the route will see, measured on the trimmed text because that is
   * what `useChatSend` puts on the wire (ORA-750). A paste over the cap is
   * refused here rather than by the server: the student sees the count and the
   * reason while they are still holding the text, instead of watching their
   * message go and a bare "Invalid turn request" come back.
   */
  const draftLength = value.trim().length;
  const isOverLengthLimit = draftLength > CHAT_MESSAGE_MAX_CHARS;
  /**
   * The text of the last send, so a server refusal can hand it back.
   *
   * The composer clears itself the moment `onSend` accepts, and only the server
   * knows the message was too long — with the client's own cap in place, that
   * means the two disagree (the backend moved its limit, or has not shipped the
   * code yet). The draft only returns to an empty box: a student who has already
   * started typing something else must not have it overwritten.
   */
  const lastSubmitted = useRef('');
  /**
   * The server's refusal is about the message it refused, so it goes the moment
   * the student edits the draft (ORA-750 review round 2). Only their own edits
   * count: the effect below puts the rejected text back programmatically, and
   * that must not dismiss the sentence explaining why it is there.
   */
  const [isRejectionDismissed, setIsRejectionDismissed] = useState(false);

  const edit = (next: string) => {
    setIsRejectionDismissed(true);
    setValue(next);
  };

  useEffect(() => {
    if (!sendRejection) return;

    setIsRejectionDismissed(false);
    setValue((current) => (current === '' ? lastSubmitted.current : current));
  }, [sendRejection]);

  // Only the empty/non-empty flip leaves the composer: the follow-up chips
  // step aside while the student is writing (ORA-606), and a per-keystroke
  // signal would re-render the thread for nothing.
  useEffect(() => {
    onDraftChange?.(hasDraft);
  }, [hasDraft, onDraftChange]);

  // Every change of the text — typed, prefilled, dictated, or cleared by a
  // send — is a reason to re-measure, so the effect follows the value rather
  // than hanging off the change handler.
  useLayoutEffect(() => {
    if (textareaRef.current) resizeComposerTextarea(textareaRef.current);
  }, [value]);

  // …and so is a change of the box's width. A box measured while hidden (the
  // phone document view's Chat tab, a closed sheet) measured nothing and was
  // set to no height at all; it gets its width back when it is shown. A
  // resized split re-wraps the text too. Height changes, including the ones
  // this makes, are not a reason.
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || typeof ResizeObserver === 'undefined') return;
    let width = textarea.getBoundingClientRect().width;

    const observer = new ResizeObserver(([entry]) => {
      const nextWidth = entry?.contentRect.width ?? 0;
      if (nextWidth === width) return;
      width = nextWidth;
      if (nextWidth > 0) resizeComposerTextarea(textarea);
    });

    observer.observe(textarea);
    return () => observer.disconnect();
  }, []);

  // A hidden drawer stays mounted. One false → true visibility edge earns
  // one focus request; readiness may arrive later, but later thread changes
  // must not take focus from a student already using another control.
  const isFocusPending = useRef(false);

  useEffect(() => {
    if (!autoFocus) {
      isFocusPending.current = false;
      return;
    }

    isFocusPending.current = true;
  }, [autoFocus]);

  useEffect(() => {
    if (!autoFocus || !isFocusPending.current || isBlocked || outOfChats) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      if (!isFocusPending.current) return;
      isFocusPending.current = false;
      focusTextarea();
    });

    return () => cancelAnimationFrame(frame);
  }, [autoFocus, focusTextarea, isBlocked, outOfChats]);

  const isMcGrawHill = useIsMcGrawHill();

  const hasSeenPhi = useSelector((state: RootState) =>
    state.messageState
      ? isTipDismissed(state, state.user?.profile, 'chat-image-phi-reminder')
      : false
  );

  const attachments = usePendingAttachments({
    disabled: isStreaming || isBlocked || outOfChats,
    notify: setNotice,
    onFirstAttach: () => {
      if (!hasSeenPhi) setPhiOpen(true);
    },
  });

  const documents = usePendingDocuments({
    disabled: isStreaming || isBlocked || outOfChats,
    // ChatComposer slices a mixed gesture before either hook adds it. This
    // callback remains a guard for direct document adds from this component.
    available: () => MAX_CHAT_IMAGES - attachments.pending.length,
    notify: setNotice,
  });

  const attachDisabled =
    isStreaming ||
    isBlocked ||
    outOfChats ||
    attachments.pending.length + documents.pending.length >= MAX_CHAT_IMAGES;

  const attachmentRefusalNotice = outOfChats
    ? 'Daily chat limit reached'
    : isStreaming || isBlocked
      ? 'Wait for the response to finish'
      : attachments.pending.length + documents.pending.length >= MAX_CHAT_IMAGES
        ? 'You can attach up to 4 files.'
        : '';

  const isImageFile = useCallback(
    (file: File) =>
      file.type.startsWith('image/') ||
      isAcceptedChatImageType(file) ||
      /\.(heic|heif)$/i.test(file.name),
    []
  );

  const addFiles = useCallback(
    (files: FileList | File[], method: PendingAttachmentMethod) => {
      const valid = Array.from(files).filter((file) => {
        if (isImageFile(file)) {
          if (!isAcceptedChatImageType(file)) {
            setNotice(
              'Please attach only PNG, JPEG, WebP, HEIC, or HEIF images.'
            );

            return false;
          }
          if (file.size > MAX_CHAT_IMAGE_BYTES) {
            setNotice('Each image must be 10 MB or smaller.');
            return false;
          }
          return true;
        }
        if (!isAcceptedChatDocument(file)) {
          setNotice('Attach a PDF, PowerPoint, Word, TXT, or CSV file.');
          return false;
        }
        if (file.size > CHAT_DOCUMENT_MAX_BYTES) {
          setNotice('Each document must be 20 MB or smaller.');
          return false;
        }
        return true;
      });

      const available =
        MAX_CHAT_IMAGES - attachments.pending.length - documents.pending.length;

      if (available <= 0) {
        setNotice('You can attach up to 4 files.');
        return;
      }
      if (valid.length > available) setNotice('You can attach up to 4 files.');

      const accepted = valid.slice(0, available);
      const images = accepted.filter(isImageFile);
      const documentFiles = accepted.filter((file) => !isImageFile(file));
      if (images.length) attachments.add(images, method);
      if (documentFiles.length) documents.add(documentFiles);
    },
    [
      attachments.add,
      attachments.pending.length,
      documents.add,
      documents.pending.length,
      isImageFile,
    ]
  );

  useImperativeHandle(
    attachmentInputRef,
    () => ({
      addFiles,
      isAtAttachmentLimit: () =>
        attachments.pending.length + documents.pending.length >=
        MAX_CHAT_IMAGES,
      showAttachmentRefusal: () => setNotice(attachmentRefusalNotice),
    }),
    [
      addFiles,
      attachmentRefusalNotice,
      attachments.pending.length,
      documents.pending.length,
    ]
  );

  const canSend =
    (value.trim().length > 0 ||
      attachments.readyRefs.length > 0 ||
      documents.readyIds.length > 0) &&
    !attachments.isUploading &&
    !documents.isPreparing &&
    !isStreaming &&
    !isBlocked &&
    !outOfChats &&
    !isOverLengthLimit &&
    // A half-finished transcript is not a message the student asked to send.
    !isDictating;

  const submit = () => {
    if (!canSend) return;

    lastSubmitted.current = value;

    const hasFailedAttachment =
      attachments.pending.some((item) => item.status === 'error') ||
      documents.pending.some((item) => item.status === 'error');

    const accepted =
      onSend(
        value.trim(),
        attachments.readyRefs,
        documents.readyIds,
        documents.readyAttachments
      ) !== false;

    if (!accepted) return;

    setNotice(
      hasFailedAttachment
        ? 'Some attachments failed to upload or prepare and were not sent.'
        : ''
    );

    documents.clear(documents.readyIds);

    attachments.clear(
      new Set(
        attachments.readyRefs.flatMap((item) =>
          item.previewUrl ? [item.previewUrl] : []
        )
      )
    );

    setValue('');
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };

  // Enter sends, Shift+Enter starts a new line — the convention every chat box
  // has trained students to expect. Enter while an IME is composing belongs to
  // the IME (it accepts the candidate), so it must not send.
  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    if (event.nativeEvent.isComposing) return;
    event.preventDefault();
    submit();
  };

  const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    if (Capacitor.isNativePlatform()) return;

    const clipboardFiles = Array.from(event.clipboardData.files);

    const itemFiles = Array.from(event.clipboardData.items)
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile())
      .filter((item): item is File => item !== null);

    const files = clipboardFiles.length > 0 ? clipboardFiles : itemFiles;

    if (files.length === 0) return;

    if (!Array.from(event.clipboardData.types).includes('text/plain'))
      event.preventDefault();

    if (attachDisabled) {
      setNotice(attachmentRefusalNotice);
      return;
    }

    addFiles(files, 'paste');
  };

  /** 36px is a comfortable pointer target; a finger needs 44. */
  const touchTargetClass = 'size-9 [@media(max-width:560px)]:size-11';

  // The static sentence while the draft is over the cap; the server's own
  // sentence for a send it refused. Neither changes as the student types, which
  // is what makes the live region below safe to read out.
  const lengthRefusal = isOverLengthLimit
    ? CHAT_MESSAGE_TOO_LONG_NOTICE
    : isRejectionDismissed
      ? null
      : sendRejection;

  return (
    <form
      onSubmit={handleSubmit}
      className="chat-motion mx-auto flex w-full max-w-3xl flex-col gap-1.5 pb-3 mobile:pb-4"
    >
      {notice ? (
        <p role="status" className="m-0 px-1 text-xs text-fg-secondary">
          {notice}
        </p>
      ) : null}
      {/*
       * Why the message cannot go, next to the box holding it (ORA-750). The
       * client's own sentence wins over the server's while the draft is over the
       * cap, because that is the problem in front of the student; a refusal of a
       * send that has already left says whatever the server said.
       */}
      {lengthRefusal ? (
        <p
          role="alert"
          data-testid="chat-composer-length-refusal"
          className="m-0 px-1 text-xs text-status-danger"
        >
          {lengthRefusal}
        </p>
      ) : null}
      <div
        data-testid="chat-composer-surface"
        className={cn(
          'flex flex-col gap-1 rounded-lg border border-border-subtle bg-surface-canvas p-1.5 shadow-low',
          // In dark mode the box is glass over the page gradient, as the
          // legacy panel's was; light mode sits on the primary surface so the
          // context pills read as highlights on top of it.
          'dark:bg-surface-glass',
          // On a phone the box and its controls share one line (ORA-628): a
          // second row of mostly-empty bar stole the little thread a keyboard
          // leaves visible. The rows above — attachments, documents, the
          // limit meter — take a full-width line of their own, and `items-end`
          // keeps the buttons on the box's last line as it grows upward.
          'max-mobile:flex-row max-mobile:flex-wrap max-mobile:items-end max-mobile:gap-x-2',
          'transition-[border-color,box-shadow] duration-150',
          'focus-within:border-ring-default focus-within:ring-2 focus-within:ring-ring-default'
        )}
      >
        {contextPills}
        {attachments.pending.length > 0 ? (
          <div
            role="group"
            aria-label="Attached images"
            className="flex flex-wrap items-center gap-2 px-1 pt-1 max-mobile:w-full"
          >
            {attachments.pending.map((item, index) => (
              <div
                key={item.localId}
                className={cn(
                  'animate-chat-pop-in flex items-center gap-1.5 rounded-xl border bg-surface-muted p-1',
                  item.status === 'error'
                    ? 'border-status-danger'
                    : 'border-border-subtle'
                )}
              >
                <span className="relative block size-10 shrink-0">
                  <img
                    src={item.previewUrl}
                    alt={`Pending image ${index + 1}`}
                    className="size-10 rounded-lg object-cover"
                  />
                  {item.status === 'uploading' ? (
                    <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-surface-canvas/80">
                      <Spinner
                        aria-label={`Uploading image ${index + 1}`}
                        size={16}
                      />
                    </span>
                  ) : null}
                </span>
                {item.status === 'error' ? (
                  <button
                    type="button"
                    disabled={isStreaming || isBlocked || outOfChats}
                    title={item.errorMessage}
                    aria-label={`Retry image ${index + 1}`}
                    onClick={() => attachments.retry(item.localId)}
                    className="flex items-center gap-1 rounded-lg px-1 text-xs text-status-danger hover:bg-surface-highlight disabled:opacity-60"
                  >
                    <ArrowClockwise size={14} aria-hidden="true" />
                    Failed
                  </button>
                ) : (
                  <span className="max-w-[8rem] truncate text-xs text-fg-secondary">
                    {item.file.name || 'Image'}
                  </span>
                )}
                <button
                  type="button"
                  aria-label={`Remove image ${index + 1}`}
                  onClick={() => attachments.remove(item.localId)}
                  className={cn(
                    'flex shrink-0 items-center justify-center rounded-lg text-fg-secondary',
                    'hover:bg-surface-highlight hover:text-fg-primary',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-default',
                    touchTargetClass
                  )}
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </div>
            ))}
            <span className="text-xs text-fg-secondary">
              {attachments.pending.length + documents.pending.length} of 4
              attachments
            </span>
          </div>
        ) : null}
        {documents.pending.length > 0 ? (
          <div
            className="mb-2 space-y-1 max-mobile:w-full"
            aria-label="Attached documents"
          >
            {documents.pending.map((document) => (
              <div
                key={document.localId}
                className="flex items-center gap-2 rounded-lg bg-surface-muted px-2 py-1.5 text-xs text-fg-primary"
              >
                <FileText
                  size={16}
                  aria-hidden="true"
                  className="shrink-0 text-fg-secondary"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{document.fileName}</span>
                  {document.status === 'error' && document.errorMessage ? (
                    <span className="block text-status-danger">
                      {document.errorMessage}
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 text-fg-secondary">
                  {document.status === 'ready'
                    ? document.pageCount
                      ? `${document.pageCount} pages`
                      : 'Ready'
                    : document.status === 'error'
                      ? 'Could not prepare'
                      : document.status === 'processing'
                        ? 'Preparing…'
                        : 'Uploading…'}
                </span>
                {document.preparationWarning ? (
                  <Tooltip title={document.preparationWarning}>
                    <Warning
                      size={15}
                      className="shrink-0 text-status-warning"
                      aria-label="Preparation warning"
                    />
                  </Tooltip>
                ) : null}
                {['error', 'processing'].includes(document.status) ? (
                  <button
                    type="button"
                    disabled={isStreaming || isBlocked || outOfChats}
                    className="min-h-9 rounded px-1 underline disabled:opacity-60 [@media(max-width:560px)]:min-h-11"
                    onClick={() => documents.retry(document.localId)}
                  >
                    {document.status === 'processing'
                      ? 'Check Status'
                      : 'Retry'}
                  </button>
                ) : null}
                <button
                  type="button"
                  aria-label={`Remove ${document.fileName}`}
                  onClick={() => documents.remove(document.localId)}
                  className="min-h-9 min-w-9 rounded p-0.5 hover:bg-surface-highlight [@media(max-width:560px)]:min-h-11 [@media(max-width:560px)]:min-w-11"
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        ) : null}
        {/* Below `mobile` the box is the flexible half of a one-line bar; the
            wrapper takes that role so the cycling hint can sit over the box. */}
        <div className="relative max-mobile:min-w-0 max-mobile:flex-1">
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(event) => edit(event.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            disabled={isDisabled}
            aria-label="Message Ora"
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={
              outOfChats
                ? 'Daily chat limit reached'
                : isBlocked
                  ? 'Loading this chat…'
                  : idlePlaceholder
            }
            rows={1}
            focusRing="none"
            // The bar owns the border and the focus ring; the box inside it is
            // only text. The `!`s are needed because the global
            // `textarea { ... }` in `sass/Components/_inputs.scss` is unlayered,
            // and unlayered CSS outranks every Tailwind utility. Its 16px font
            // size is deliberately left alone: that is what stops iOS zooming
            // the page when the box takes focus.
            className={cn(
              'min-h-0 resize-none',
              '[background-color:transparent]! [border:0]!',
              '[padding:0.375rem_0.5rem]!',
              'focus-visible:[box-shadow:none] focus-visible:ring-0'
            )}
          />
          {/* The lead stays put; the ending drops out of the line and the
              next one drops in from above (`chat-cycle-*`), so the swap reads
              as one downward cycle. Sized and padded like the box's own text
              so it sits exactly where the placeholder would. On a phone the
              box shares its line with the buttons and the whole hint does not
              fit, so the ending (never the lead) ends in an ellipsis. */}
          {isCycleShown ? (
            <div
              aria-hidden="true"
              data-testid="chat-composer-cycling-placeholder"
              className="chat-motion pointer-events-none absolute inset-0 flex px-2 py-1.5 font-sans text-base leading-normal text-fg-secondary opacity-75"
            >
              <span className="shrink-0 whitespace-pre">{cycle.lead}</span>
              <span
                key={cyclingPlaceholder.index}
                data-testid="chat-composer-cycling-placeholder-tail"
                className={cn(
                  'min-w-0 truncate',
                  cyclingPlaceholder.isFading
                    ? 'animate-chat-cycle-out'
                    : 'animate-chat-cycle-in'
                )}
              >
                {cyclingPlaceholder.text}
              </span>
            </div>
          ) : null}
        </div>
        {/*
         * `contents` below `mobile` (ORA-628): the controls keep their own row
         * from `mobile` up, and on a phone their buttons join the text box on
         * the bar's one line without a second DOM order to maintain.
         */}
        <div className="flex items-center gap-1 max-mobile:contents">
          {!Capacitor.isNativePlatform() ? (
            <>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept={`${CHAT_IMAGE_ACCEPT},.pdf,.ppt,.pptx,.doc,.docx,.txt,.csv`}
                hidden
                onChange={(event) => {
                  if (event.target.files) {
                    const files = Array.from(event.target.files);

                    addFiles(files, 'picker');
                  }
                  event.target.value = '';
                }}
              />
              <DropdownMenu>
                <Tooltip title={attachmentRefusalNotice || 'Add attachment'}>
                  <span>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Add attachment"
                        disabled={attachDisabled}
                        className={cn(
                          'rounded-full transition-transform active:scale-[.94]',
                          touchTargetClass
                        )}
                      >
                        <Plus size={18} aria-hidden="true" />
                      </Button>
                    </DropdownMenuTrigger>
                  </span>
                </Tooltip>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onSelect={() => inputRef.current?.click()}>
                    <Image size={16} aria-hidden="true" />
                    Add images
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => inputRef.current?.click()}>
                    <Paperclip size={16} aria-hidden="true" />
                    Add documents
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : null}
          {/*
           * `ml-auto` lives on this group, not on the voice button: that
           * renders nothing without the Web Speech API (iOS's WKWebView), and a
           * spacer gone with it left the send button on the bar's left.
           */}
          <div className="ml-auto flex min-w-0 items-center gap-2 max-mobile:contents">
            <ChatVoiceInput
              value={value}
              onChange={edit}
              disabled={isStreaming || isBlocked || outOfChats}
              onFocusInput={focusTextarea}
              onActiveChange={setIsDictating}
            />
            {/* Only once it matters: a running counter on every draft would be
                noise on a box students normally type one sentence into. Hidden
                from assistive tech because the alert above has already read out
                both numbers in a sentence. */}
            {isOverLengthLimit ? (
              <span
                aria-hidden="true"
                data-testid="chat-composer-length-count"
                className="shrink-0 text-xs tabular-nums text-status-danger"
              >
                {chatMessageLengthCount(draftLength)}
              </span>
            ) : null}
            <ChatLimitMeter limits={limits} />
            {isStreaming ? (
              <Button
                type="button"
                variant="inverse"
                size="icon"
                shape="round"
                onClick={onStop}
                aria-label="Stop response"
                className={cn(
                  'transition-transform active:scale-[.94]',
                  touchTargetClass
                )}
              >
                <Stop size={16} weight="fill" aria-hidden="true" />
              </Button>
            ) : (
              <Button
                type="submit"
                variant="inverse"
                size="icon"
                shape="round"
                disabled={!canSend}
                aria-label="Send message"
                className={cn(
                  'transition-transform enabled:active:scale-[.94]',
                  !canSend && 'bg-surface-muted text-fg-secondary',
                  touchTargetClass
                )}
              >
                <ArrowUp size={16} aria-hidden="true" />
              </Button>
            )}
          </div>
        </div>
      </div>
      {/*
       * Under every composer surface (page, drawer, floating shell): the
       * legacy chat's line, lost with its panel in ORA-455.
       */}
      <p
        data-testid="chat-composer-disclaimer"
        className="m-0! px-1 text-center text-xxs text-fg-secondary"
      >
        Ora AI can make mistakes. Check important info.
        {!isMcGrawHill && ' Ora AI is not a clinical tool.'}
      </p>
      <ChatImagePhiReminderModal
        open={phiOpen}
        onClose={() => {
          setPhiOpen(false);
          void markTipSeen('chat-image-phi-reminder');
        }}
      />
    </form>
  );
};

export default ChatComposer;
